'use client';

import { PlayIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { IconButton } from '../../../components/ui/icon-button';
import { cn } from '../../../lib/utils';
import type { LandingVideoContent } from '../types';

export type LandingVideoPlayerProps = {
  video: LandingVideoContent;
  className?: string;
  /** Figma methodology section uses a rounded-square play affordance. */
  playButtonVariant?: 'circle' | 'rounded-square';
};

export function LandingVideoPlayer({
  video,
  className,
  playButtonVariant = 'circle',
}: LandingVideoPlayerProps) {
  const hasEmbed = Boolean(video.embedUrl?.trim());
  const closeLabel = video.closeLabel ?? 'Close';
  /** Hidden once the user's click lands inside the (cross-origin) Drive player. */
  const [started, setStarted] = useState(false);
  /** Bumping this remounts the iframe so closing actually stops playback. */
  const [reloadKey, setReloadKey] = useState(0);

  // We can't read inside the cross-origin Drive iframe, but when the user's
  // click passes through the cover and lands on the player, the iframe gains
  // focus. That focus is our signal that playback was triggered, so we drop the
  // smiling cover to reveal the now-playing video instead of Drive's thumbnail.
  useEffect(() => {
    if (!hasEmbed || started) return;
    function revealOnIframeFocus() {
      if (document.activeElement?.tagName === 'IFRAME') setStarted(true);
    }
    window.addEventListener('blur', revealOnIframeFocus);
    return () => window.removeEventListener('blur', revealOnIframeFocus);
  }, [hasEmbed, started]);

  // No embed configured: show the poster, and surface the placeholder on click.
  if (!hasEmbed) {
    return <NoEmbedPlayer video={video} className={className} />;
  }

  return (
    <div
      className={cn(
        'relative aspect-video w-[90%] overflow-hidden rounded-2xl border border-border bg-black shadow-lg',
        className,
      )}
    >
      <iframe
        key={reloadKey}
        src={video.embedUrl}
        title={video.title}
        className='absolute inset-0 size-full'
        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
        allowFullScreen
        onFocus={() => setStarted(true)}
      />

      {!started ? (
        // pointer-events-none lets the single click pass through to Drive's
        // centered play button underneath, so the cover only masks the
        // unwanted Drive thumbnail — it never intercepts the click itself.
        <div
          className='pointer-events-none absolute inset-0 z-10'
          aria-hidden
        >
          {/* eslint-disable-next-line @next/next/no-img-element -- external poster URL */}
          <img
            src={video.posterUrl}
            alt=''
            className='absolute inset-0 size-full object-cover'
          />
          <div className='absolute inset-0 bg-linear-to-t from-background/75 via-background/20 to-background/10' />
          <div className='absolute inset-0 flex items-center justify-center'>
            <span
              className={cn(
                'flex items-center justify-center bg-[#2D2138] text-primary-500 shadow-lg',
                playButtonVariant === 'rounded-square'
                  ? 'size-16 rounded-2xl sm:size-18'
                  : 'size-16 rounded-full bg-primary-600 text-primary-foreground sm:size-20',
              )}
            >
              <PlayIcon
                className={cn(
                  'ml-0.5',
                  playButtonVariant === 'rounded-square'
                    ? 'size-7 sm:size-8'
                    : 'size-8 sm:size-10',
                )}
                aria-hidden
              />
            </span>
          </div>
        </div>
      ) : (
        <IconButton
          type='button'
          variant='ghost'
          className='absolute right-3 top-3 z-10 rounded-full bg-background/80 text-foreground backdrop-blur-sm hover:bg-background [&_svg]:size-5'
          aria-label={closeLabel}
          onClick={() => {
            setStarted(false);
            setReloadKey((key) => key + 1);
          }}
        >
          <XMarkIcon aria-hidden />
        </IconButton>
      )}
    </div>
  );
}

/** Fallback player when no embed URL is configured (poster + placeholder). */
function NoEmbedPlayer({
  video,
  className,
}: {
  video: LandingVideoContent;
  className?: string;
}) {
  const [playing, setPlaying] = useState(false);
  const closeLabel = video.closeLabel ?? 'Close';

  if (playing) {
    return (
      <div
        className={cn(
          'relative flex aspect-video w-full flex-col items-center justify-center gap-4 overflow-hidden rounded-2xl border border-border bg-card px-6 text-center shadow-lg',
          className,
        )}
      >
        {video.placeholderMessage ? (
          <p className='max-w-md text-base text-muted-foreground'>
            {video.placeholderMessage}
          </p>
        ) : null}
        <Button
          type='button'
          variant='outline'
          size='md'
          className='border-foreground/30'
          onClick={() => setPlaying(false)}
        >
          {closeLabel}
        </Button>
      </div>
    );
  }

  return (
    <button
      type='button'
      className={cn(
        'group relative aspect-video w-full overflow-hidden rounded-2xl border border-border bg-muted shadow-lg',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
        className,
      )}
      onClick={() => setPlaying(true)}
      aria-label={`Play video: ${video.title}`}
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- external poster URL */}
      <img
        src={video.posterUrl}
        alt=''
        className='absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-[1.02] motion-reduce:transition-none'
      />
      <div
        className='absolute inset-0 bg-linear-to-t from-background/75 via-background/20 to-background/10'
        aria-hidden
      />
      <span className='absolute inset-0 flex items-center justify-center'>
        <span className='flex size-16 items-center justify-center rounded-full bg-primary-600 text-primary-foreground shadow-lg transition-transform group-hover:scale-105 motion-reduce:transition-none sm:size-20'>
          <PlayIcon className='ml-0.5 size-8 sm:size-10' aria-hidden />
        </span>
      </span>
      <span className='sr-only'>{video.title}</span>
    </button>
  );
}
